import { useCallback, useEffect, useRef, useState } from "react";

import * as service from "../services/notificationService";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export const useNotifications = (enabled = true) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const unreadIdsRef = useRef(new Set());
  const pendingReadIdsRef = useRef(new Set());
  const markAllPendingRef = useRef(false);

  const dataVersionRef = useRef(0);
  const countRequestRef = useRef(0);
  const listRequestRef = useRef(0);

  const refreshUnreadCount = useCallback(async () => {
    if (!enabled) return false;

    const requestId = ++countRequestRef.current;
    const dataVersion = dataVersionRef.current;

    try {
      const res = await service.getUnreadCount();

      const count = Math.max(0, Number(res?.data?.unreadCount ?? 0));

      if (
        requestId === countRequestRef.current &&
        dataVersion === dataVersionRef.current
      ) {
        setUnreadCount(Number.isFinite(count) ? count : 0);
      }

      return count;
    } catch (requestError) {
      console.error("refreshUnreadCount failed:", requestError);

      return false;
    }
  }, [enabled]);

  const getNotifications = useCallback(async () => {
    if (!enabled) return false;

    const requestId = ++listRequestRef.current;
    const dataVersion = dataVersionRef.current;

    try {
      setLoading(true);
      setError("");

      const res = await service.getNotifications(20, 0);

      if (!res?.data?.success) {
        throw new Error("Failed to load notifications.");
      }

      const items = Array.isArray(res.data.notifications)
        ? res.data.notifications
        : [];

      if (
        requestId === listRequestRef.current &&
        dataVersion === dataVersionRef.current
      ) {
        const normalizedItems = items.map((item) => ({
          ...item,
          isRead: Number(item.isRead) === 1 ? 1 : 0,
        }));

        unreadIdsRef.current = new Set(
          normalizedItems
            .filter((item) => item.isRead === 0)
            .map((item) => String(item.notificationId)),
        );

        setNotifications(normalizedItems);

        setUnreadCount(Math.max(0, Number(res.data.unreadCount ?? 0)));

        return normalizedItems;
      }

      return items;
    } catch (requestError) {
      console.error("getNotifications failed:", requestError);

      if (requestId === listRequestRef.current) {
        setError(
          getErrorMessage(requestError, "Failed to load notifications."),
        );
      }

      return false;
    } finally {
      if (requestId === listRequestRef.current) {
        setLoading(false);
      }
    }
  }, [enabled]);

  const markAsRead = useCallback(
    async (notificationId) => {
      if (!enabled || markAllPendingRef.current) {
        return false;
      }

      const id = Number(notificationId);

      if (!Number.isSafeInteger(id) || id <= 0) {
        return false;
      }

      const notificationKey = String(id);

      if (pendingReadIdsRef.current.has(notificationKey)) {
        return false;
      }

      pendingReadIdsRef.current.add(notificationKey);

      try {
        setError("");

        const res = await service.markNotificationAsRead(id);

        if (!res?.data?.success) {
          throw new Error("Failed to mark notification as read.");
        }

        dataVersionRef.current += 1;

        const wasUnread = unreadIdsRef.current.has(notificationKey);

        unreadIdsRef.current.delete(notificationKey);

        setNotifications((current) =>
          current.map((item) =>
            Number(item.notificationId) === id
              ? {
                  ...item,
                  isRead: 1,
                }
              : item,
          ),
        );

        if (wasUnread) {
          setUnreadCount((current) => Math.max(0, current - 1));
        }

        await refreshUnreadCount();

        return true;
      } catch (requestError) {
        console.error("markAsRead failed:", requestError);

        setError(
          getErrorMessage(requestError, "Failed to mark notification as read."),
        );

        return false;
      } finally {
        pendingReadIdsRef.current.delete(notificationKey);
      }
    },
    [enabled, refreshUnreadCount],
  );

  const markAllAsRead = useCallback(async () => {
    if (!enabled || markAllPendingRef.current) {
      return false;
    }

    markAllPendingRef.current = true;

    try {
      setError("");

      const res = await service.markAllNotificationsAsRead();

      if (!res?.data?.success) {
        throw new Error("Failed to mark all notifications as read.");
      }

      dataVersionRef.current += 1;

      unreadIdsRef.current.clear();

      setNotifications((current) =>
        current.map((item) => ({
          ...item,
          isRead: 1,
        })),
      );

      setUnreadCount(0);

      await refreshUnreadCount();

      return true;
    } catch (requestError) {
      console.error("markAllAsRead failed:", requestError);

      setError(
        getErrorMessage(
          requestError,
          "Failed to mark all notifications as read.",
        ),
      );

      return false;
    } finally {
      markAllPendingRef.current = false;
    }
  }, [enabled, refreshUnreadCount]);

  useEffect(() => {
    if (!enabled) {
      dataVersionRef.current += 1;
      countRequestRef.current += 1;
      listRequestRef.current += 1;

      unreadIdsRef.current.clear();

      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      setError("");

      return;
    }

    void refreshUnreadCount();

    const intervalId = window.setInterval(() => {
      void refreshUnreadCount();
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [enabled, refreshUnreadCount]);

  return {
    notifications,
    unreadCount,

    loading,
    error,

    getNotifications,
    refreshUnreadCount,

    markAsRead,
    markAllAsRead,
  };
};

export default useNotifications;
